import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  CircularProgress,
  Button,
} from "@mui/material";
import {
  getBillingApiStatus,
  refreshQuickbooksToken,
} from "../../../shared/billing.api.js";

export function ApiStatusBox({ snackbarRef }) {
  const [apiStatus, setApiStatus] = useState(null);
  const [apiStatusLoading, setApiStatusLoading] = useState(false);
  const [apiStatusError, setApiStatusError] = useState(null);
  const [isRefreshingToken, setIsRefreshingToken] = useState(false);

  useEffect(() => {
    setApiStatusLoading(true);
    setApiStatusError(null);
    getBillingApiStatus()
      .then((res) => {
        setApiStatus(res.data);
        setApiStatusLoading(false);
      })
      .catch((e) => {
        setApiStatusError(e.message || "Failed to load API status");
        setApiStatusLoading(false);
      });
  }, []);

  const handleRefreshQuickBooksToken = () => {
    setIsRefreshingToken(true);
    refreshQuickbooksToken()
      .then((response) => {
        setIsRefreshingToken(false);
        snackbarRef?.current?.addSnackbar?.({
          type: "success",
          message: `Successfully refreshed QuickBooks token. ${JSON.stringify(response?.data)}`,
          persist: true,
        });
        console.log("QuickBooks token response", response);
      })
      .catch((error) => {
        setIsRefreshingToken(false);
        snackbarRef?.current?.addSnackbar?.({
          type: "error",
          message: `Error refreshing QuickBooks token: ${JSON.stringify(error?.response)}`,
          persist: true,
        });
        console.log("Error refreshing QuickBooks token", error);
      });
  };

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        background: "#f9f9f9",
      }}
    >
      <Typography variant="h6" sx={{ mb: 1 }}>
        API Status
      </Typography>
      {apiStatusLoading ? (
        <Stack direction="row" alignItems="center" spacing={1}>
          <CircularProgress size={18} />
          <Typography variant="body2">Checking API status...</Typography>
        </Stack>
      ) : apiStatusError ? (
        <Typography color="error">{apiStatusError}</Typography>
      ) : apiStatus ? (
        <Stack spacing={1}>
          <Typography variant="body2">
            <strong>QuickBooks:</strong>{" "}
            {apiStatus.quickbooks === "connected" ? (
              <span style={{ color: "#3bb143" }}>Connected</span>
            ) : apiStatus.quickbooks.startsWith("error") ? (
              <span style={{ color: "#d32f2f" }}>{apiStatus.quickbooks}</span>
            ) : (
              apiStatus.quickbooks
            )}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              handleRefreshQuickBooksToken();
            }}
            disabled={isRefreshingToken}
            endIcon={isRefreshingToken ? <CircularProgress size={14} /> : null}
          >
            Refresh QuickBooks Token
          </Button>
          <Typography variant="body2">
            <strong>Postgres:</strong>{" "}
            {apiStatus.postgres === "connected" ? (
              <span style={{ color: "#3bb143" }}>Connected</span>
            ) : apiStatus.postgres.startsWith("error") ? (
              <span style={{ color: "#d32f2f" }}>{apiStatus.postgres}</span>
            ) : (
              apiStatus.postgres
            )}
          </Typography>
          <Typography variant="body2">
            <strong>Stripe:</strong>{" "}
            {apiStatus.stripe === "connected" ? (
              <span style={{ color: "#3bb143" }}>Connected</span>
            ) : apiStatus.stripe.startsWith("error") ? (
              <span style={{ color: "#d32f2f" }}>{apiStatus.stripe}</span>
            ) : (
              apiStatus.stripe
            )}
          </Typography>
          <Typography variant="body2">
            <strong>Snowflake:</strong>{" "}
            {apiStatus.snowflake === "connected" ? (
              <span style={{ color: "#3bb143" }}>Connected</span>
            ) : apiStatus.snowflake.startsWith("error") ? (
              <span style={{ color: "#d32f2f" }}>{apiStatus.snowflake}</span>
            ) : (
              apiStatus.snowflake
            )}
          </Typography>
          <Typography variant="body2">
            <strong>Pandadoc:</strong>{" "}
            {apiStatus.pandadoc === "connected" ? (
              <span style={{ color: "#3bb143" }}>Connected</span>
            ) : apiStatus.pandadoc && apiStatus.pandadoc.startsWith("error") ? (
              <span style={{ color: "#d32f2f" }}>{apiStatus.pandadoc}</span>
            ) : (
              (apiStatus.pandadoc ?? "--")
            )}
          </Typography>
          <Typography variant="body2">
            <strong>Overall:</strong>{" "}
            {apiStatus.overall === "connected" ? (
              <span style={{ color: "#3bb143" }}>All Systems Operational</span>
            ) : (
              <span style={{ color: "#d32f2f" }}>Error</span>
            )}
          </Typography>
        </Stack>
      ) : null}
    </Box>
  );
}
